import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getPostBySlug } from './blogData';
import AiPhoneAgentsArticle from './articles/AiPhoneAgentsArticle';
import CrmIntegrationArticle from './articles/CrmIntegrationArticle';
import AiVsHumanArticle from './articles/AiVsHumanArticle';
import SeoClusterArticle, { articleBodies } from './articles/SeoClusterArticle';
import SEO, { SITE_URL } from '../SEO';
import Footer from '../Footer';
import './Blog.css';

const articleComponents = {
  'ai-phone-agents-save-la-restaurants-17k-year': AiPhoneAgentsArticle,
  'crm-integration-small-businesses-complete-guide': CrmIntegrationArticle,
  'ai-vs-human-call-handling-2026-comparison': AiVsHumanArticle,
  'best-ai-receptionist-los-angeles-businesses': SeoClusterArticle,
  'ai-appointment-booking-dental-offices-los-angeles': SeoClusterArticle,
  'ai-intake-agents-law-firms-los-angeles': SeoClusterArticle,
  'how-much-does-an-ai-receptionist-cost': SeoClusterArticle,
  'ai-receptionist-vs-answering-service': SeoClusterArticle,
  'do-ai-phone-agents-actually-work': SeoClusterArticle,
  'ai-receptionist-for-small-business-guide': SeoClusterArticle,
  'how-many-calls-do-small-businesses-miss': SeoClusterArticle,
  'ai-receptionist-spanish-bilingual': SeoClusterArticle,
};

function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const ArticleContent = articleComponents[slug];

  return (
    <>
      <SEO
        title={`${post.title} | UBOTIKA`}
        description={post.description}
        path={`/blog/${post.slug}`}
        image={`${SITE_URL}/og/${post.slug}.png`}
        type="article"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: post.title,
            description: post.description,
            datePublished: post.date,
            dateModified: post.dateModified || post.date,
            image: post.image,
            author: { '@type': 'Organization', name: 'UBOTIKA', url: SITE_URL },
            publisher: {
              '@type': 'Organization',
              '@id': `${SITE_URL}/#organization`,
              name: 'UBOTIKA',
              logo: {
                '@type': 'ImageObject',
                url: `${SITE_URL}/ubotika-logo.png`,
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `${SITE_URL}/blog/${post.slug}`,
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
              { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
              { '@type': 'ListItem', position: 3, name: post.title, item: `${SITE_URL}/blog/${post.slug}` },
            ],
          },
          articleBodies[post.slug]?.faqs?.length
            ? {
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: articleBodies[post.slug].faqs.map((item) => ({
                  '@type': 'Question',
                  name: item.q,
                  acceptedAnswer: { '@type': 'Answer', text: item.a },
                })),
              }
            : null,
        ].filter(Boolean)}
      />
      <main id="main-content" className="blog-post-page">
      <article className="blog-article">
        <div className="blog-container">
          <Link to="/blog" className="blog-back-link">
            &larr; Back to Blog
          </Link>
          <motion.header
            className="blog-post-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>{post.title}</h1>
            <div className="blog-post-meta">
              <time dateTime={post.date}>{post.date}</time>
              {post.dateModified && post.dateModified !== post.date && (
                <time dateTime={post.dateModified}>Updated {post.dateModified}</time>
              )}
              <span>{post.readTime}</span>
              <span>{post.author}</span>
            </div>
          </motion.header>
          <motion.div
            className="article-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <ArticleContent post={post} />
          </motion.div>
          <div className="blog-post-footer">
            <Link to="/blog" className="blog-back-link">
              &larr; Back to Blog
            </Link>
          </div>
        </div>
      </article>
      <Footer />
      </main>
    </>
  );
}

export default BlogPost;
